// Constantes PRODUIT partagées — source de vérité unique côté front.
//
// ⚠️ NB_THEMATIQUES : l'observatoire sert TREIZE thématiques (13ᵉ « Revenus » ouverte le 15/09,
// décision Marc — `dem-5` sort de Démographie et devient `rev-1` ; 12ᵉ « Équipements » ouverte le
// 07/09, THEME_EQU=1 en prod) depuis la création
// d'« Environnement & risques » (décision Marc du 03/08). Le « 10 » écrit en dur à
// seize endroits a sous-vendu le produit pendant trois jours (audit vue d'ensemble,
// V-A) : nous facturions un abonnement « tout ou rien » en annonçant un module de
// moins que ce que le client recevait.
//
// La 13e thématique se déclare ICI — et dans les fichiers STATIQUES qui ne peuvent pas
// importer cette constante (les mettre à jour à la main, la liste est courte) :
//   · frontend/index.html            (og:description + schema.org ×2)
//   · frontend/public/llms.txt       (description pour les crawlers LLM)
//   · backend/app/ventes_pdf.py      compte DYNAMIQUEMENT depuis tagging (rien à faire)
// Le catalogue d'ÉTUDES a son propre référentiel (10 thèmes, dont agriculture et
// participation) : ses mentions disent « thématiques d'études », à ne pas confondre.
//
// ⚠️ A20 (08/10) — repli hors ligne ; la vérité vient de /api/observatoire/structure
// (`useStructure()`, structure.ts) et, dans l'observatoire, de `/meta` (`nb_themes`). Le
// nombre de thématiques suit désormais un drapeau SERVEUR (`ARBORESCENCE_V2` : 20 allumé,
// 13 éteint) ; ce « 13 » n'est lu que le temps du chargement ou si l'API ne répond pas.
// Ne PAS le passer à 20 : un retour arrière par le drapeau ferait mentir la vitrine.
export const NB_THEMATIQUES = 13

// #151 (08/08) — la source de vérité des NOMS, pas seulement du nombre : les
// énumérations recopiées ont divergé (AboutPage servait 9 noms sur 11 — sécurité et
// environnement manquaient — et aucun grep sur « 10 thématiques » ne pouvait le voir).
// L'ordre est celui de la vitrine. Toute page qui énumère les thématiques passe par
// ICI ; le catalogue d'ÉTUDES a son propre référentiel (10, dont agriculture et
// participation) et ne consomme PAS cette liste.
// A20 : comme `NB_THEMATIQUES`, un repli hors ligne — et le texte de l'arborescence v1, que
// `nomsThematiques()` (structure.ts) rend tel quel tant que le serveur sert la v1.
export const THEMATIQUES = [
  'démographie', 'revenus', 'logement', 'finances', 'solidarités', 'sécurité', 'santé',
  'mobilité', 'emploi', 'énergie', 'éducation', 'environnement', 'équipements',
] as const

// ⚠️ NOM DE MARQUE — centralisé le 07/08, BASCULÉ le 13/09 (LObsTer → TerObs ; nom
// tranché par Marc et Cyrille le 01/08, la marque LObsTer étant bloquée par une
// antériorité). La centralisation a tenu sa promesse : les surfaces STRUCTURELLES
// (titre d'onglet, meta, marque de la topbar et du pied de page, tunnel, fiches
// techniques, pied du PDF, gates) ont basculé par CETTE ligne. La prose marketing
// (Landing, FAQ, About, Collectivités, Accessibilité) et les surfaces statiques
// (index.html, llms.txt, robots.txt) ont été reprises à la main le même jour.
//
// ⚠️⚠️ LA MARQUE A BASCULÉ, LE DOMAINE NON. Le site dit « TerObs » et reste servi sur
// `l-obster.fr` : la bascule DNS attend Marc (`dig terobs.fr` ne rend aucun A/CNAME).
// Cet écart est VOULU, et il est gardé par `marque.test.ts` — qui échoue aussi bien
// sur un « LObsTer » oublié que sur un `terobs.fr` recopié trop tôt. Le second est le
// plus coûteux : il ne casse rien de visible. Une og:image sur un domaine sans DNS
// tue le partage de lien en silence ; une adresse `@terobs.fr` dont personne n'a
// confirmé que la boîte est relevée fait écrire une collectivité dans le vide.
//
// Restent VOLONTAIREMENT en LObsTer — ce n'est pas un oubli :
//   · le DOMAINE et les ADRESSES : `l-obster.fr`, `contact@` / `noreply@` / `dpo@`,
//     les URL absolues, la canonique, l'og:image et le sitemap. Ils basculent le jour
//     de la migration DNS, pas avant ;
//   · (PLUS VRAI DEPUIS LE 21/09, issue #319) les pages LÉGALES. On attendait la
//     création de la société pour les réécrire en bloc ; elles ont donc gardé huit jours
//     un nom que le reste du site avait quitté, dans le seul document opposable. La
//     dénomination sociale est TerObs et ne dépendait d'aucune immatriculation. La
//     PERSONNE MORALE se nomme désormais par `SOCIETE.raisonSociale` (societe.ts), le
//     PRODUIT par `NOM_PRODUIT` ci-dessous — deux constantes parce que ce sont deux
//     choses, et qu'elles peuvent diverger un jour ;
//   · les identifiants techniques (clés localStorage `lobster_*`, classes Pydantic
//     `RapportLObsTer`, champ `etudes_lobster`, conteneurs `lobster-db` /
//     `lobster-backend`) — les renommer déconnecte les sessions ouvertes et casse le
//     contrat JSON du LLM ;
//   · le laboratoire public/labo/** (maquettes datées, derrière basic_auth, non indexées) ;
//   · le BACKEND, qui a sa propre marque en dur (gabarits PDF des études, prompts LLM,
//     libellés Stripe, courriels transactionnels). Il n'a PAS basculé : un PDF d'étude
//     et un courriel de commande disent encore « LObsTer ». Lot à part — il touche des
//     prompts porteurs d'un contrat JSON et demande sa propre recette.
export const NOM_PRODUIT = 'TerObs'

// ⚠️ LE DOMAINE — lu dans l'environnement de BUILD, écrit nulle part ailleurs.
//
// La marque a basculé le 13/09 ; le domaine bascule le jour J, et ce jour-là personne
// ne doit relire deux cents fichiers. `VITE_SITE_DOMAIN` arrive par l'argument de build
// du Dockerfile (docker-compose.prod.yml), avec pour défaut le domaine où le site répond
// AUJOURD'HUI — la seule valeur sûre : un build sans variable sert une adresse joignable
// au lieu d'un domaine sans DNS.
// Défaut basculé le 15/10 : la prod sert terobs.fr (l-obster.fr y redirige en 301).
export const DOMAINE_PUBLIC = import.meta.env.VITE_SITE_DOMAIN || 'terobs.fr'
export const SITE_PUBLIC = `https://${DOMAINE_PUBLIC}`

// ⚠️ LES ADRESSES ont leur PROPRE variable, et ce n'est pas un oubli. Les MX de
// terobs.fr existent chez Infomaniak, mais personne n'a confirmé que les boîtes sont
// relevées. Basculer le web est réversible en deux minutes ; afficher une adresse morte
// sur une page lue par une mairie ne l'est pas — elle écrit, n'obtient rien, et rien
// nulle part ne le signale. Le jour où Marc a REÇU un courriel de test, c'est une ligne.
// Défaut basculé le 15/10 : les courriels partent de terobs.fr depuis le 13/10.
export const DOMAINE_COURRIEL = import.meta.env.VITE_EMAIL_DOMAIN || 'terobs.fr'
export const EMAIL_CONTACT = `contact@${DOMAINE_COURRIEL}`
export const EMAIL_DPO = `dpo@${DOMAINE_COURRIEL}`

// Les domaines que la prod SERT réellement — la liste que `marque.test.ts` autorise dans
// une URL absolue. Elle vaut garde-fou : tant que `terobs.fr` n'y figure pas, l'écrire
// dans une canonique, une og:image ou un lien fait échouer le build.
//
// 🔁 LE JOUR J, C'EST CETTE LIGNE : ajouter 'terobs.fr'. Les deux domaines cohabitent
// pendant J → J+30 (l'ancien répond en 301, cf. frontend/Caddyfile), donc on AJOUTE,
// on ne remplace pas.
// Jour J passé : terobs.fr est servi ; l-obster.fr répond encore en 301 jusqu'à son
// expiration (27/03/2027) — à retirer de la liste ce jour-là.
export const DOMAINES_SERVIS = ['terobs.fr', 'l-obster.fr'] as const
